import { soundKeys } from "../audio/sounds";
import {
  createDefeatOverlay,
  createPauseOverlay,
  createQueueOverlay,
  createUpgradeOverlay
} from "../app_overlays";
import type { GameSnapshot } from "../render/renderer";
import {
  DEFAULT_END_SCREEN_TIMEOUT_MS,
  createGameplayUi,
  type SessionMode,
  type SessionOptions,
  type SessionState
} from "./session_helpers";

export function toggleGameMenuOverlay(options: SessionOptions, state: SessionState): void {
  if (
    state.currentSnapshot?.defeated ||
    state.respawning ||
    state.gameEndResult ||
    state.queueOverlay
  ) {
    return;
  }

  if (state.menuOpen) {
    closeGameMenuOverlay(options, state, true);
    return;
  }

  options.audio.play(soundKeys.buttonClick);
  state.menuOpen = true;

  state.pauseOverlay?.destroy();
  state.pauseOverlay = createPauseOverlay({
    title: options.i18n.t("pause.title"),
    resumeText: options.i18n.t("pause.resume"),
    menuText: options.i18n.t("pause.menu"),
    onResume: () => {
      closeGameMenuOverlay(options, state, true);
    },
    onMenu: () => {
      options.audio.play(soundKeys.buttonClick);
      state.menuOpen = false;
      state.pauseOverlay?.destroy();
      state.pauseOverlay = null;
      options.onReturnToMenu();
    }
  });

  options.root.append(state.pauseOverlay.element);
}

export function showQueueOverlay(
  options: SessionOptions,
  state: SessionState,
  message: string
): void {
  state.menuOpen = false;
  state.pauseOverlay?.destroy();
  state.pauseOverlay = null;
  state.upgradeOverlay?.destroy();
  state.upgradeOverlay = null;
  state.defeatOverlay?.destroy();
  state.defeatOverlay = null;
  state.gameEndOverlay?.destroy();
  state.gameEndOverlay = null;

  if (state.queueOverlay) {
    state.queueOverlay.setMessage(message);
    return;
  }

  state.queueOverlay = createQueueOverlay({
    title: options.i18n.t("queue.title"),
    message,
    menuText: options.i18n.t("queue.menu"),
    onMenu: () => {
      options.audio.play(soundKeys.buttonClick);
      options.onReturnToMenu();
    }
  });

  options.root.append(state.queueOverlay.element);
}

export function clearQueueOverlay(state: SessionState): void {
  state.queueOverlay?.destroy();
  state.queueOverlay = null;
}

export function syncUpgradeOverlay(
  options: SessionOptions,
  state: SessionState,
  mode: SessionMode
): void {
  const upgrades = getPendingUpgrades(state.currentSnapshot);

  if (
    state.queueOverlay ||
    state.currentSnapshot?.defeated ||
    upgrades.length === 0 ||
    state.respawning ||
    state.gameEndResult
  ) {
    state.upgradeOverlay?.destroy();
    state.upgradeOverlay = null;
    state.upgradeSignature = "";
    return;
  }

  const signature = JSON.stringify(upgrades);

  if (state.upgradeOverlay && state.upgradeSignature === signature) {
    return;
  }

  state.upgradeOverlay?.destroy();
  state.upgradeSignature = signature;

  state.upgradeOverlay = createUpgradeOverlay({
    title: options.i18n.t("upgrade.title"),
    upgrades,
    i18n: options.i18n,
    playerBallColor: options.profile.ballColor,
    onChoose: (upgrade) => {
      options.audio.play(soundKeys.buttonClick);

      if (mode === "offline") {
        state.offlineGame?.chooseUpgrade(upgrade);
        state.currentSnapshot = state.offlineGame?.snapshot() as GameSnapshot | null;
        state.renderSnapshot = state.currentSnapshot;
      } else {
        state.onlineClient?.chooseUpgrade(upgrade);
      }

      state.upgradeOverlay?.destroy();
      state.upgradeOverlay = null;
      state.upgradeSignature = "";

      state.renderer?.render(
        state.renderSnapshot ?? state.currentSnapshot,
        createGameplayUi(options, state)
      );
    }
  });

  options.root.append(state.upgradeOverlay.element);
}

export function syncDefeatOverlay(
  options: SessionOptions,
  state: SessionState,
  mode: SessionMode
): void {
  if (state.queueOverlay || state.gameEndResult) {
    state.defeatSoundPlayed = false;
    state.defeatOverlay?.destroy();
    state.defeatOverlay = null;
    return;
  }

  const defeated = Boolean(state.currentSnapshot?.defeated);

  if (!defeated || state.respawning) {
    state.defeatSoundPlayed = false;
    state.defeatTimeoutMs = null;
    state.defeatOverlay?.destroy();
    state.defeatOverlay = null;
    return;
  }

  state.menuOpen = false;
  state.pauseOverlay?.destroy();
  state.pauseOverlay = null;
  state.upgradeOverlay?.destroy();
  state.upgradeOverlay = null;
  state.upgradeSignature = "";

  if (!state.defeatSoundPlayed) {
    options.audio.play(soundKeys.death);
    state.defeatSoundPlayed = true;
  }

  if (state.defeatOverlay) {
    return;
  }

  const timeoutOptions =
    mode === "online"
      ? {
          timeoutMs: state.defeatTimeoutMs ?? DEFAULT_END_SCREEN_TIMEOUT_MS,
          onTimeout: () => {
            options.onReturnToMenu();
          }
        }
      : {};

  state.defeatOverlay = createDefeatOverlay({
    title: options.i18n.t("defeat.title"),
    subtitle: options.i18n.t("defeat.subtitle"),
    respawnText: options.i18n.t("defeat.respawn"),
    menuText: options.i18n.t("defeat.menu"),
    ...timeoutOptions,
    onRespawn: () => {
      respawn(options, state, mode);
    },
    onMenu: () => {
      options.audio.play(soundKeys.buttonClick);
      options.onReturnToMenu();
    }
  });

  options.root.append(state.defeatOverlay.element);
}

export function syncGameEndOverlay(options: SessionOptions, state: SessionState): void {
  const gameEnd = state.gameEndResult;

  if (!gameEnd) {
    state.gameEndOverlay?.destroy();
    state.gameEndOverlay = null;
    return;
  }

  state.menuOpen = false;
  clearQueueOverlay(state);
  state.pauseOverlay?.destroy();
  state.pauseOverlay = null;
  state.upgradeOverlay?.destroy();
  state.upgradeOverlay = null;
  state.upgradeSignature = "";
  state.defeatOverlay?.destroy();
  state.defeatOverlay = null;
  state.defeatSoundPlayed = false;

  if (state.gameEndOverlay) {
    return;
  }

  state.gameEndOverlay = createDefeatOverlay({
    title: gameEnd.won ? options.i18n.t("gameEnd.winTitle") : options.i18n.t("gameEnd.title"),
    subtitle: options.i18n.t("gameEnd.subtitle"),
    respawnText: options.i18n.t("defeat.respawn"),
    menuText: options.i18n.t("defeat.menu"),
    timeoutMs: gameEnd.timeoutMs,
    onRespawn: () => {
      continueAfterGameEnd(options, state);
    },
    onMenu: () => {
      options.audio.play(soundKeys.buttonClick);
      options.onReturnToMenu();
    },
    onTimeout: () => {
      options.onReturnToMenu();
    }
  });

  options.root.append(state.gameEndOverlay.element);
}

function closeGameMenuOverlay(
  options: SessionOptions,
  state: SessionState,
  playSound: boolean
): void {
  if (!state.menuOpen && !state.pauseOverlay) {
    return;
  }

  if (playSound) {
    options.audio.play(soundKeys.buttonClick);
  }

  state.menuOpen = false;
  state.pauseOverlay?.destroy();
  state.pauseOverlay = null;
}

function respawn(options: SessionOptions, state: SessionState, mode: SessionMode): void {
  if (state.respawning) {
    return;
  }

  options.audio.play(soundKeys.buttonClick);

  state.respawning = true;
  state.hasMoved = false;
  state.defeatSoundPlayed = false;
  state.defeatTimeoutMs = null;
  state.menuOpen = false;

  state.pauseOverlay?.destroy();
  state.pauseOverlay = null;
  state.defeatOverlay?.destroy();
  state.defeatOverlay = null;
  state.upgradeOverlay?.destroy();
  state.upgradeOverlay = null;
  state.upgradeSignature = "";

  if (mode === "offline") {
    const success = state.offlineGame?.respawn() ?? false;

    if (success) {
      state.currentSnapshot = state.offlineGame?.snapshot() as GameSnapshot | null;
      state.renderSnapshot = state.currentSnapshot;
    }

    state.respawning = false;
  } else {
    state.snapshotInterpolator?.clear();
    state.renderSnapshot = state.currentSnapshot;
    state.onlineClient?.respawn();
  }

  state.renderer?.render(
    state.renderSnapshot ?? state.currentSnapshot,
    createGameplayUi(options, state)
  );
}

function continueAfterGameEnd(options: SessionOptions, state: SessionState): void {
  if (state.respawning) {
    return;
  }

  options.audio.play(soundKeys.buttonClick);

  state.gameEndResult = null;
  state.defeatTimeoutMs = null;
  state.gameEndOverlay?.destroy();
  state.gameEndOverlay = null;
  state.hasMoved = false;
  state.menuOpen = false;
  state.respawning = true;
  state.currentSnapshot = null;
  state.renderSnapshot = null;
  state.snapshotInterpolator?.clear();
  state.onlineClient?.respawn();

  state.renderer?.render(null, createGameplayUi(options, state));
}

function getPendingUpgrades(snapshot: GameSnapshot | null): unknown[] {
  if (!snapshot) {
    return [];
  }

  return snapshot.pending_upgrades ?? snapshot.pendingUpgrades ?? [];
}